import { Link } from 'react-router-dom'
import {Phone, Droplets, Trash2, Zap, Flame, Clock, ShieldCheck, CheckCircle2, AlertTriangle, Calendar, Thermometer} from 'lucide-react'
import ContactForm from '@/components/ContactForm'

const services = [
  {
    id: 'fuite',
    icon: Droplets,
    title: "Fuite d'eau",
    desc: "Détection et réparation rapide de toute fuite : robinetterie, canalisation, raccord, dégât des eaux.",
    points: ["Localisation précise", "Réparation immédiate", "Intervention sans casse"],
  },
  {
    id: 'wc',
    icon: Trash2,
    title: "WC bouché",
    desc: "Débouchage rapide de WC obstrué avec matériel professionnel, sans dégât pour vos installations.",
    points: ["Furet professionnel", "Sans produit chimique agressif", "Vérification complète"],
  },
  {
    id: 'canalisation',
    icon: Zap,
    title: "Canalisation bouchée",
    desc: "Débouchage haute pression et inspection caméra pour identifier et résoudre la cause du bouchage.",
    points: ["Hydrocurage", "Caméra d'inspection", "Diagnostic précis"],
  },
  {
    id: 'chauffe-eau',
    icon: Flame,
    title: "Chauffe-eau en panne",
    desc: "Dépannage, détartrage ou remplacement de chauffe-eau toutes marques, intervention le jour même.",
    points: ["Toutes marques", "Pièces en stock", "Remplacement rapide"],
  },
  {
    id: 'chauffage',
    icon: Thermometer,
    title: "Chauffage en panne",
    desc: "Dépannage chaudière, radiateurs froids, perte de pression, problème de thermostat. Intervention rapide pour retrouver la chaleur, été comme hiver.",
    points: ["Chaudière gaz / mazout / électrique", "Purge & remise en pression", "Toutes marques (Vaillant, Viessmann, Buderus, Hoval...)"],
  },
]

export default function EmergencyPage() {
  return (
    <div>
      <section className="relative bg-navy-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 90% 0%, white 0%, transparent 60%)' }} />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-urgent-500/20 border border-urgent-400/40 text-urgent-300 text-xs font-bold uppercase tracking-wider">
              <AlertTriangle className="w-3.5 h-3.5" />
              Urgence sanitaire
            </div>
            <h1 className="mt-5 text-4xl lg:text-6xl font-extrabold tracking-tight text-balance leading-[1.05]">
              Dépannage plomberie d'urgence <span className="text-urgent-400">24h/24, 7j/7</span>
            </h1>
            <p className="mt-5 text-lg text-navy-100 leading-relaxed">
              Fuite, WC bouché, canalisation, chauffe-eau ou chauffage en panne ? Notre équipe intervient rapidement partout en Suisse romande, jour et nuit.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <a href="tel:+41796430399" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl gradient-urgent text-white font-bold shadow-orange-glow">
                <Phone className="w-5 h-5" strokeWidth={2.5} />
                Appeler maintenant
              </a>
              <a href="https://fixnow.ch" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-white text-navy-700 font-bold hover:bg-navy-50 transition-colors">
                <Calendar className="w-5 h-5" strokeWidth={2.5} />
                Voir notre disponibilité sur FixNow
              </a>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-xl">
              <div className="text-center sm:text-left">
                <div className="text-2xl lg:text-3xl font-extrabold text-urgent-400">35 min</div>
                <div className="text-xs text-navy-200 font-semibold mt-0.5">Délai moyen</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-2xl lg:text-3xl font-extrabold text-urgent-400">24/7</div>
                <div className="text-xs text-navy-200 font-semibold mt-0.5">Disponibilité</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-2xl lg:text-3xl font-extrabold text-urgent-400">5000+</div>
                <div className="text-xs text-navy-200 font-semibold mt-0.5">Interventions</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
            <div className="space-y-6">
              {services.map((s) => (
                <div key={s.id} id={s.id} className="bg-white rounded-2xl p-6 lg:p-8 border border-navy-100 shadow-soft scroll-mt-24">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl gradient-urgent flex items-center justify-center flex-shrink-0">
                      <s.icon className="w-7 h-7 text-white" strokeWidth={2.2} />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl lg:text-2xl font-extrabold text-navy-700">{s.title}</h2>
                      <p className="mt-2 text-navy-500 leading-relaxed">{s.desc}</p>
                      <ul className="mt-4 space-y-2">
                        {s.points.map((p) => (
                          <li key={p} className="flex items-center gap-2 text-sm font-semibold text-navy-600">
                            <CheckCircle2 className="w-4 h-4 text-urgent-500 flex-shrink-0" strokeWidth={2.5} />
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}

              <div className="bg-navy-50 border border-navy-100 rounded-2xl p-6 lg:p-8">
                <h3 className="text-lg lg:text-xl font-extrabold text-navy-700 mb-4">Pourquoi nous choisir ?</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-urgent-500 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
                    <div>
                      <div className="font-bold text-navy-700 text-sm">Rapidité garantie</div>
                      <div className="text-xs text-navy-500 mt-0.5">Intervention sous 1 heure dans la plupart des cas</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-urgent-500 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
                    <div>
                      <div className="font-bold text-navy-700 text-sm">Devis transparent</div>
                      <div className="text-xs text-navy-500 mt-0.5">Tarifs annoncés avant intervention</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:sticky lg:top-28 self-start">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
